
import { supabase } from "@/integrations/supabase/client";
import { getPromoCodesFromStorage } from './storage';
// Hook import removed - function receives getProductPrice as parameter

interface CartItem {
  id: number;
  price: number;
  quantity: number;
}

interface PromoEligibleItem {
  id: number;
}

/**
 * Calculates discount amount based on promo code and cart items using current (sale) prices
 */
export const calculateDiscount = async (
  promoCode: string, 
  cartItems: CartItem[],
  getProductPrice?: (productId: number) => number
): Promise<{ discount: number; eligibleProductIds: number[] }> => {
  console.log('calculateDiscount called with:', promoCode, 'items:', cartItems.length);
  
  if (!promoCode || !cartItems.length) {
    console.log('No promo code or empty cart, returning 0 discount');
    return { discount: 0, eligibleProductIds: [] };
  }
  
  try {
    // Use narrow validate_promo_code RPC which performs server-side validation
    // and returns only the minimal fields needed for discount calculation.
    const { data, error } = await supabase.rpc('validate_promo_code', { code_param: promoCode });

    if (error || !data) {
      console.error('Error validating promo code for discount calculation:', error);
      // Fall back to storage-cached codes (client-known only)
      const allPromoCodes = await getPromoCodesFromStorage();
      const validPromo = allPromoCodes.find(
        (promo) => promo.code.toLowerCase() === promoCode.toLowerCase()
      );
      if (!validPromo) return { discount: 0, eligibleProductIds: [] };
      if (validPromo.expiryDate && new Date() > new Date(validPromo.expiryDate)) {
        return { discount: 0, eligibleProductIds: [] };
      }
      return calculateDiscountAmount(validPromo, cartItems, getProductPrice);
    }

    const result = data as any;
    if (!result.valid) {
      console.log('Promo code invalid:', result.error);
      return { discount: 0, eligibleProductIds: [] };
    }

    return calculateDiscountAmount(
      {
        discount_percentage: result.discount_percentage,
        selected_products: result.selected_products,
      },
      cartItems,
      getProductPrice
    );
  } catch (error) {
    console.error('Error calculating discount:', error);
    return { discount: 0, eligibleProductIds: [] };
  }
};


/**
 * Helper function to calculate discount amount using current prices
 */
const calculateDiscountAmount = (
  promoData: any,
  cartItems: CartItem[],
  getProductPrice?: (productId: number) => number
): { discount: number; eligibleProductIds: number[] } => {
  // Parse selected products with safety checks
  let selectedProducts: any[] = [];
  try {
    const rawSelectedProducts = promoData.selected_products || promoData.selectedProducts;
    selectedProducts = typeof rawSelectedProducts === 'string'
      ? JSON.parse(rawSelectedProducts)
      : Array.isArray(rawSelectedProducts) ? rawSelectedProducts : [];
      
    console.log('Parsed selected products:', selectedProducts);
  } catch (e) {
    console.error('Error parsing selected products:', e);
  }
  
  const discountPercentage = promoData.discount_percentage || promoData.discountPercentage || 0;
  
  // If specific products are selected
  if (Array.isArray(selectedProducts) && selectedProducts.length > 0) {
    const eligibleProductIds = selectedProducts.map((p: any) => p.id);
    console.log('Eligible product IDs:', eligibleProductIds);
    
    // Filter cart items to only include those that match eligible product IDs
    const eligibleItems = cartItems.filter(item => eligibleProductIds.includes(item.id));
    console.log('Eligible items from cart:', eligibleItems);
    
    // Calculate total price of eligible items using current (sale) prices
    const eligibleTotal = eligibleItems.reduce((total, item) => {
      // Use current price from pricing system if available, otherwise use item price
      const currentPrice = getProductPrice ? getProductPrice(item.id) : item.price;
      console.log(`Item ${item.id}: current price ${currentPrice}, original price ${item.price}`);
      return total + (currentPrice * item.quantity);
    }, 0);
    
    console.log('Eligible total with current prices:', eligibleTotal, 'discount percentage:', discountPercentage);
    
    // Apply discount only to eligible items
    const discountAmount = (eligibleTotal * discountPercentage) / 100;
    console.log('Calculated discount amount for specific products:', discountAmount);
    
    return { 
      discount: discountAmount,
      eligibleProductIds: eligibleProductIds
    };
  }
  
  // If the promo code applies to all products
  const cartTotal = cartItems.reduce((total, item) => {
    // Use current price from pricing system if available, otherwise use item price
    const currentPrice = getProductPrice ? getProductPrice(item.id) : item.price;
    return total + (currentPrice * item.quantity);
  }, 0);
  
  console.log('Cart total with current prices:', cartTotal, 'discount percentage:', discountPercentage);
  
  const discountAmount = (cartTotal * discountPercentage) / 100;
  console.log('Calculated discount amount for all products:', discountAmount);
  
  return { 
    discount: discountAmount,
    eligibleProductIds: cartItems.map(item => item.id)
  };
};
